import {
  AllOption,
  ComplianceTypes,
  ETHICS_FIELDS,
  EthicsField,
  SortByOptions,
} from '@/constants';
import { ComplianceType, Episode, EpisodeCard, EpisodeFacet } from '@/types';

export const getIsEpisodeEthics = ({
  episode,
  compliance,
}: {
  episode?: Episode | EpisodeCard;
  compliance: ComplianceType;
}) => {
  // Parse ethics info from newer credit structure, if present
  if (episode?.creditSummary) {
    return compliance === ComplianceTypes.CPE
      ? ETHICS_FIELDS.includes(
          episode?.creditSummary.find(info => info.type === 'cpe')?.fieldOfStudy
            ?.id as EthicsField,
        )
      : (episode?.creditSummary.find(info => info.type === 'cpd')
          ?.ethicsHours ?? 0) > 0;
  }

  return compliance === ComplianceTypes.CPE
    ? ETHICS_FIELDS.includes(episode?.fieldOfStudyId as EthicsField)
    : (episode?.totalEthics ?? 0) > 0;
};

export const getRegionApiQueryParams = ({
  audienceRegions,
}: {
  audienceRegions?: string[];
}) => {
  if (audienceRegions?.length)
    return {
      audience_regions: `ov.{${audienceRegions.join(',')}}`,
    };

  return {};
};

export const getFacetFilters = (filters?: Record<string, string[]>) => ({
  ...(filters?.region && !filters.region?.includes(AllOption.key)
    ? { audience_region: filters.region }
    : {}),
  ...(filters?.format && !filters.format?.includes(AllOption.key)
    ? { episode_format: filters.format }
    : {}),
  ...(filters?.mediaType && !filters.mediaType?.includes(AllOption.key)
    ? { media_type: filters.mediaType }
    : {}),
  ...(filters?.audienceFunction &&
  !filters.audienceFunction?.includes(AllOption.key)
    ? { audience_function: filters.audienceFunction }
    : {}),
});

export const getSortByQuery = (sortBy?: string) => {
  switch (sortBy) {
    case SortByOptions.ALPHABETICAL.key:
      return { order: 'name.asc' };
    case SortByOptions.DURATION_ASC.key:
      return { order: 'duration.asc' };
    case SortByOptions.DURATION_DESC.key:
      return { order: 'duration.desc' };
    case SortByOptions.CREDIT_ASC.key:
      return { order: 'credits->1->credits.asc.nullslast' };
    case SortByOptions.CREDIT_DESC.key:
      return { order: 'credits->1->credits.desc.nullslast' };
    default:
      return { order: 'publish_date.desc' };
  }
};

export const getFacetOptions = (facet?: EpisodeFacet, flat?: boolean) =>
  facet?.values.reduce(
    (acc, value) => ({
      ...acc,
      [value.slug]: flat ? value.value : { text: value.value },
    }),
    {},
  ) || {};

export const getFacetValueText = (
  facet: EpisodeFacet,
  activeSlugs?: string[],
) => {
  const activeValues = facet?.values.filter(value =>
    activeSlugs?.includes(value.slug),
  );

  return activeValues && activeValues.length > 0
    ? `${facet.name === 'Audience Function' ? 'Function' : facet.name}: ${activeValues[0].value}${activeValues.length > 1 ? ` +${activeValues.length - 1}` : ''}`
    : facet.name;
};
